import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Footer from './Footer';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import { formatToHumanDateTime } from '../utils/DateFormatter';

function PhoneSmsLogs(props) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [countries, setCountries] = useState(null);
    const [provinces, setProvinces] = useState(null);
    const [districts, setDistricts] = useState(null);
    const [villages, setVillages] = useState(null);
    const [filteredProvinces, setFilteredProvinces] = useState(null);
    const [filteredDistricts, setFilteredDistricts] = useState(null);
    const [filteredVillages, setFilteredVillages] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('2');
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedVillage, setSelectedVillage] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [formError, setFormError] = useState(null);
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const [selectedType, setSelectedType] = useState('');

    useEffect(() => {
        if (!isAuthenticated) {
            window.location.href = '/login';
        }
    }, [isAuthenticated, navigate]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');
        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);
        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }
        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };

    const handleApiError = (error, response) => {
        if (
            response &&
            response.status === 401 &&
            error.message.includes('Your authentication token is invalid')
        ) {
            setTimeout(() => {
                logout();
                window.location.href = '/login';
            }, 0);
            return 'Session expired. Please log in again.';
        }
        return error.message || 'An error occurred. Please try again.';
    };

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/locations/country`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const data = await response.json();
                setCountries(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };
        if (isAuthenticated) fetchCountries();
    }, [isAuthenticated]);

    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const data = await response.json();
                setProvinces(data.data || []);
                setFilteredProvinces(data.data || []);
                setSelectedProvince(null);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        const fetchDistricts = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const data = await response.json();
                setDistricts(data.data || []);
                setFilteredDistricts(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        const fetchVillages = async () => {
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const data = await response.json();
                setVillages(data.data || []);
                setFilteredVillages(data.data || []);
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        if (selectedCountry && isAuthenticated) {
            fetchProvinces();
            fetchDistricts();
            fetchVillages();
        }
    }, [selectedCountry, isAuthenticated]);

    useEffect(() => {
        if (districts && selectedProvince) {
            setFilteredDistricts(
                districts.filter((district) => district.adm1_id === parseInt(selectedProvince))
            );
            setSelectedDistrict(null);
        } else {
            setFilteredDistricts(null);
            setSelectedDistrict(null);
            setFilteredVillages(null);
            setSelectedVillage(null);
        }
    }, [selectedProvince, districts]);

    useEffect(() => {
        if (villages && selectedDistrict) {
            setFilteredVillages(
                villages.filter((village) => village.adm2_id === parseInt(selectedDistrict))
            );
            setSelectedVillage(null);
        } else {
            setFilteredVillages(null);
        }
    }, [selectedDistrict, villages]);

    const fetchPhoneLogs = async (forExport = false, typeOverride = undefined) => {
        if (!selectedCountry) return;

        setIsLoading(true);
        setFetchError(null);
        if (!activeTab) setActiveTab('active');

        const effectiveType = typeOverride !== undefined ? typeOverride : selectedType;
        try {
            let url = `${envConfig.API_URL}/advisory/message_log/phone_numbers`;
            const params = new URLSearchParams({ country_id: selectedCountry });

            if (selectedProvince) params.append('adm1_id', selectedProvince);
            if (selectedDistrict) params.append('adm2_id', selectedDistrict);
            if (selectedVillage) params.append('adm3_id', selectedVillage);
            if (selectedStartDate) params.append('start_date', selectedStartDate);
            if (selectedEndDate) params.append('end_date', selectedEndDate);
            if (effectiveType) params.append('type', effectiveType);

            if (!forExport) {
                params.append('page', pageNo);
                params.append('size', perPage);
            } else {
                params.append('size', '10000');
            }

            url += `?${params.toString()}`;

            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Error: ${response.status}`);

            const data = await response.json();
            if (data.success === 1) {
                if (forExport) return data.data || [];
                setTableData(data.data || []);
                const newTotalRows = data.metadata?.total_count || data.data.length;
                setTotalRows(newTotalRows);
            } else {
                throw new Error('API request failed');
            }
        } catch (error) {
            setFetchError(handleApiError(error, error.response));
            setTableData([]);
            setTotalRows(0);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        if (!selectedCountry) {
            setFormError('Country selection is mandatory.');
            return;
        }
        setFormError(null);
        setPageNo(1);
        fetchPhoneLogs();
    };

    const onCountryChange = (e) => {
        setSelectedCountry(e.target.value);
        setProvinces(null);
        setDistricts(null);
        setVillages(null);
        setFilteredProvinces(null);
        setFilteredDistricts(null);
        setFilteredVillages(null);
        setSelectedProvince(null);
        setSelectedDistrict(null);
        setSelectedVillage(null);
    };

    const handleExportData = async () => {
        if (!selectedCountry) {
            Swal.fire({
                title: 'Country Required',
                text: 'Please select a country before exporting data.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        try {
            Swal.fire({
                title: 'Preparing Export...',
                text: 'Fetching data for export. Please wait...',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const allRows = await fetchPhoneLogs(true);

            if (!allRows || !allRows.length) {
                Swal.fire({
                    title: 'No Data',
                    text: 'No Phone SMS Logs available to export.',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }

            const campOrEpaLabel = selectedCountry == 1 ? 'EPA' : 'Camp';

            const exportData = allRows.map((row, index) => ({
                SNo: index + 1,
                'Phone Number': row.phone_number || '--',
                'Name': row.name || '--',
                'Message': row.message?.replace(/\n/g, ' ') || '--',
                'Ref. Date': formatToHumanDateTime(row.ref_date) || '--',
                'Sent On': row.sent_on ? formatToHumanDateTime(row.sent_on) : '--',
                'SMS Status': row.pushed_status ?? '--',
                Country: row.country || '--',
                Province: row.adm1 || '--',
                District: row.adm2 || '--',
                [campOrEpaLabel]: row.adm3 || '--',
                'Risk Level': row.risk_level ?? '--',
                'Type': row.weekly ? 'Weekly' : 'Daily',
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            worksheet['!cols'] = Object.keys(exportData[0]).map((key) => ({
                wch: Math.max(15, key.length + 5),
            }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Phone SMS Logs Export');

            const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const filename = `PhoneSmsLog_Country_${selectedCountry}_${timestamp}.xlsx`;

            XLSX.writeFile(workbook, filename);

            Swal.fire({
                title: 'Export Successful!',
                text: `Data has been exported as ${filename}.`,
                icon: 'success',
                confirmButtonText: 'OK',
            });
        } catch (error) {
            Swal.fire({
                title: 'Export Failed',
                text: error.message || 'An unexpected error occurred while exporting data.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const columns = [
        {
            name: 'Phone Number',
            selector: (row) => row.phone_number || '--',
            sortable: true,
            width: '150px',
        },
        {
            name: 'Name',
            selector: (row) => row.name || '--',
            sortable: true,
            width: '140px',
        },
        {
            name: 'Message',
            selector: (row) => row.message || '--',
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', lineHeight: '1.3' }}>
                    {row.message || '--'}
                </div>
            ),
        },
        {
            name: 'Ref. Date',
            selector: (row) => formatToHumanDateTime(row.ref_date) || '--',
            sortable: true,
            width: '110px',
            cell: (row) => <span style={{ whiteSpace: 'normal', overflow: 'visible' }}>{formatToHumanDateTime(row.ref_date) || '--'}</span>,
        },
        {
            name: 'Sent On',
            selector: (row) => formatToHumanDateTime(row.sent_on) || '--',
            sortable: true,
            width: '150px',
            cell: (row) => <span style={{ whiteSpace: 'normal', overflow: 'visible' }}>{formatToHumanDateTime(row.sent_on) || '--'}</span>,
        },
        {
            name: 'SMS Status',
            selector: (row) => row.pushed_status ?? '--',
            sortable: true,
            width: '110px',
            cell: (row) => {
                if (row.pushed_status === 'sent') return <span style={{ padding: '2px 6px', borderRadius: '3px', background: '#2e7d32', color: '#fff', fontSize: '11px' }}>Sent</span>;
                if (row.pushed_status === 'failed') return <span style={{ padding: '2px 6px', borderRadius: '3px', background: '#c62828', color: '#fff', fontSize: '11px' }}>Failed</span>;
                return <span style={{ color: '#999', fontSize: '11px' }}>--</span>;
            },
        },
        {
            name: 'Country',
            selector: (row) => row.country || '--',
            sortable: true,
            width: '90px',
        },
        {
            name: 'Province',
            selector: (row) => row.adm1 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'District',
            selector: (row) => row.adm2 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: selectedCountry == 1 ? 'EPA' : 'Camp',
            selector: (row) => row.adm3 || '--',
            sortable: true,
            width: '100px',
        },
        {
            name: 'Risk Level',
            selector: (row) => row.risk_level ?? '--',
            sortable: true,
            width: '120px',
        },
        {
            name: 'Type',
            selector: (row) => row.weekly ? 'Weekly' : 'Daily',
            sortable: true,
            width: '80px',
        },
    ];

    const handlePageChange = (page) => {
        setPageNo(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setPageNo(page);
    };

    useEffect(() => {
        if (isAuthenticated && selectedCountry && pageNo && perPage) {
            fetchPhoneLogs();
        }
    }, [isAuthenticated, pageNo, perPage]);

    const handleTypeChange = (type) => {
        setSelectedType(type);
        setPageNo(1);
        if (activeTab) fetchPhoneLogs(false, type);
    };

    const customStyles = {
        table: { style: { border: '1px solid #dee2e6' } },
        headRow: {
            style: {
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 'bold',
                borderBottom: '1px solid #dee2e6',
            },
        },
        headCells: {
            style: { padding: '10px 15px!important', borderRight: '1px solid #dee2e6' },
        },
        cells: {
            style: {
                padding: '5px 5px!important',
                borderRight: '1px solid #dee2e6',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        rows: {
            style: {
                maxHeight: '80px',
                '&:hover': { backgroundColor: '#f1f1f1' },
            },
        },
        pagination: {
            style: {
                padding: '4px 8px',
                minHeight: '40px',
                border: 'none',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                backgroundColor: '#e8f5e9',
            },
            pageButtonsStyle: {
                backgroundColor: 'transparent',
                border: 'none',
                color: '#000',
                padding: '0.25rem 0.5rem',
                margin: '0 0.25rem',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#d4edda' },
                '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
            },
        },
    };

    return (
        <>
            <div className="wrapper pt-100px one">
                <div className="container-fluid">
                    <div className="row pt-3">
                        <div className="col-sm-12 col-md-12 col-lg-12">
                            <div className="box-config">
                                <h5>Phone SMS Logs</h5>
                                <form onSubmit={handleFilterSubmit} className="">
                                    <div className="row mb-3 align-items-end">
                                        <div className="col-sm-12 col-md-11 col-lg-11">
                                            <div className="row">
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="country">Country <span className="text-danger">*</span></label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedCountry || ''}
                                                            onChange={onCountryChange}
                                                        >
                                                            <option value="">Select</option>
                                                            {countries && countries.map((country) => (
                                                                <option key={country.country_id} value={country.country_id}>
                                                                    {country.country || country.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="province">Province</label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedProvince || ''}
                                                            onChange={(e) => setSelectedProvince(e.target.value)}
                                                            disabled={!selectedCountry}
                                                        >
                                                            <option value="">Select</option>
                                                            {filteredProvinces && filteredProvinces.map((province) => (
                                                                <option key={province.adm1_id} value={province.adm1_id}>
                                                                    {province.adm1 || province.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="district">District</label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedDistrict || ''}
                                                            onChange={(e) => setSelectedDistrict(e.target.value)}
                                                            disabled={!selectedProvince}
                                                        >
                                                            <option value="">Select</option>
                                                            {filteredDistricts && filteredDistricts.map((district) => (
                                                                <option key={district.adm2_id} value={district.adm2_id}>
                                                                    {district.adm2 || district.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="village">{selectedCountry == 1 ? 'EPA' : 'Camp'}</label>
                                                        <select
                                                            className="form-select custom-select"
                                                            value={selectedVillage || ''}
                                                            onChange={(e) => setSelectedVillage(e.target.value)}
                                                            disabled={!selectedDistrict}
                                                        >
                                                            <option value="">Select</option>
                                                            {filteredVillages && filteredVillages.map((village) => (
                                                                <option key={village.adm3_id} value={village.adm3_id}>
                                                                    {village.adm3 || village.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="startDate">Start Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={selectedStartDate || ''}
                                                            onChange={(e) => setSelectedStartDate(e.target.value)}
                                                            max={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-md-2">
                                                    <div className="form-group me-2">
                                                        <label htmlFor="endDate">End Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={selectedEndDate || ''}
                                                            onChange={(e) => setSelectedEndDate(e.target.value)}
                                                            max={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                                            min={selectedStartDate}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-1 col-lg-1">
                                            <button type="submit" className="btn btn-apply" style={{ height: '36px', width: '100%', marginTop: '-26px' }}>
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </form>
                                {formError && <div className="text-danger mt-2">{formError}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12 col-md-12 col-lg">
                            <div className="filter-box border p-2" style={{ borderRight: '0px', height: 'auto' }}>
                                <h5 className="text-capitalize">Phone SMS Logs</h5>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        {activeTab && (
                                            <div className="btn-group btn-group-sm" role="group">
                                                {[['', 'All'], ['daily', 'Daily'], ['weekly', 'Weekly']].map(([val, label]) => (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        className={`btn ${selectedType === val ? 'btn-success' : 'btn-outline-secondary'}`}
                                                        onClick={() => handleTypeChange(val)}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div>
                                            <button
                                                className="btn btn-new ms-2 pe-2"
                                                onClick={handleExportData}
                                                style={{ marginBottom: '23px' }}
                                                disabled={!selectedCountry}
                                                title="Export all data to Excel"
                                            >
                                                <span>
                                                    <img
                                                        src="./assets/images/download.svg"
                                                        alt=""
                                                        style={{ height: '21px' }}
                                                    />
                                                </span>
                                                Export Data
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <DataTable
                                    key={activeTab}
                                    columns={columns}
                                    data={tableData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={pageNo}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                    paginationPerPage={perPage}
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Records per page:',
                                        rangeSeparatorText: 'of',
                                        selectAllRowsItem: false,
                                    }}
                                    customStyles={customStyles}
                                    noDataComponent={<div className="text-center p-2 font-14px">No Phone SMS Logs found</div>}
                                    highlightOnHover
                                    dense
                                    progressPending={isLoading}
                                    progressComponent={<div className="text-center p-2 font-14px">Loading...</div>}
                                />
                                {fetchError && <div className="text-danger mt-2">{fetchError}</div>}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default PhoneSmsLogs;
